import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Ticker from "./components/Ticker";
import { Helmet } from "react-helmet-async";
import BundlesExplainer from "./components/BundlesExplainer";
import BannerSlider from "./components/BannerSlider";
import CartDrawer from "./components/CartDrawer";
import AdminLogin from "./admin/AdminLogin";
import AdminDashboard from "./admin/AdminDashboard";
import ActivateAdmin from "./admin/ActivateAdmin";
import BlogSection from "./components/BlogSection";
import ProductCatalog from "./components/ProductCatalog";
import SeriesCards from "./components/SeriesCards";
import SeriesPage from "./pages/SeriesPage";
import ProductDetails from "./pages/ProductDetails";
import About from "./pages/About";
import Contact from "./pages/Contact";
import News from "./pages/News";
import BlogPost from "./pages/BlogPost";
import Checkout from "./pages/Checkout";
import OrderTracking from "./pages/OrderTracking";
import LocalBookstores from "./pages/LocalBookstores";
import BundleDetails from "./pages/BundleDetails";
import Canvas from "./pages/Canvas";
import OneTimeDownload from "./pages/OneTimeDownload";
import AnalyticsTracker from "./components/AnalyticsTracker";
import SEO from "./components/SEO";
import WhatsAppBubble from "./components/WhatsAppBubble";
import { db } from "./firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "./context/AuthContext";

/* ── Default Config ────────────────────────────────────── */
const DEFAULT_TICKER = [
    "NEW RELEASE: 'DARK VOWS' OUT NOW",
    "FREE SHIPPING ON ORDERS OVER $50",
    "SIGNED COPIES AVAILABLE"
];

const DEFAULT_SLIDES = [
    {
        imageUrl: "https://images.unsplash.com/photo-1543004218-ee141104338e?auto=format&fit=crop&q=80&w=2000",
        title: "Welcome to LB Books",
        subtitle: "Bold stories for bold readers.",
        buttonText: "Shop New Releases",
        buttonLink: "/shop"
    }
];

function Home({ slides, height, generalConfig, bundleSection, seriesCards }) {
    const siteName = generalConfig?.siteName || "LB Books";
    const siteTitle = generalConfig?.siteTitle || siteName;
    // Only show slides that have an image — filter out incomplete ones
    const validSlides = (slides || []).filter(s => s.imageUrl && s.imageUrl.trim() !== "");
    return (
        <main>
            <SEO pageId="home" />
            <BannerSlider 
                slides={validSlides.length > 0 ? validSlides : DEFAULT_SLIDES} 
                height={height || 500} 
            />
            <SeriesCards cards={seriesCards} />
            <BlogSection generalConfig={generalConfig} />
            <BundlesExplainer config={bundleSection} />
        </main>
    );
}

/* ── Protected admin route ─────────────────────────────── */
function AdminRoute() {
    const { user, isAdmin, loading } = useAuth();
    if (loading) return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "'Inter', sans-serif", color: "#888" }}>
            Loading…
        </div>
    );
    if (!user) return <AdminLogin />;
    if (!isAdmin) return (
        <div style={{ textAlign: "center", padding: "120px 20px", fontFamily: "'Inter', sans-serif" }}>
            <h2 style={{ color: "#c0392b", marginBottom: 12 }}>Access Denied</h2>
            <p style={{ color: "#666" }}>Your account does not have admin privileges.</p>
        </div>
    );
    return <AdminDashboard />;
}

/* ── Public store shell ────────────────────────────────── */
function StoreLayout({ children, tickerItems, generalConfig }) {
    return (
        <div className="app-container">
            <Ticker items={(tickerItems && tickerItems.length > 0) ? tickerItems : DEFAULT_TICKER} />
            <Navbar generalConfig={generalConfig} />
            <CartDrawer />
            {children}
            <Footer generalConfig={generalConfig} />
            <WhatsAppBubble />
        </div>
    );
}

/* ── Root App ──────────────────────────────────────────── */
export default function App() {
    const [homeConfig, setHomeConfig] = React.useState({ tickerItems: [], bannerSlides: [] });
    const [generalConfig, setGeneralConfig] = React.useState({ siteTitle: "LB Books" });

    React.useEffect(() => {
        // Real-time listener for homepage
        const unsubHome = onSnapshot(
            doc(db, "siteConfig", "homepage"),
            (snap) => {
                if (snap.exists()) setHomeConfig(snap.data());
            }
        );

        // Real-time listener for general site settings
        const unsubGeneral = onSnapshot(
            doc(db, "siteConfig", "general"),
            (snap) => {
                if (snap.exists()) setGeneralConfig(snap.data());
            }
        );

        return () => {
            unsubHome();
            unsubGeneral();
        };
    }, []);

    // Inject dynamic theme colors
    React.useEffect(() => {
        if (!generalConfig) return;

        const { 
            primaryColor, secondaryColor, accentColor, 
            textColor, backgroundColor 
        } = generalConfig;

        let styleTag = document.getElementById("dynamic-theme");
        if (!styleTag) {
            styleTag = document.createElement("style");
            styleTag.id = "dynamic-theme";
            document.head.appendChild(styleTag);
        }

        styleTag.innerHTML = `
            :root {
                ${primaryColor ? `--green-primary: ${primaryColor};` : ""}
                ${secondaryColor ? `--green-dark: ${secondaryColor};` : ""}
                ${accentColor ? `--accent: ${accentColor};` : ""}
                ${textColor ? `--text-dark: ${textColor};` : ""}
                ${backgroundColor ? `--white: ${backgroundColor};` : ""}
                ${generalConfig.featuredBg ? `--featured-bg: ${generalConfig.featuredBg};` : ""}
                ${generalConfig.featuredText ? `--featured-text: ${generalConfig.featuredText};` : ""}
                ${generalConfig.featuredAccent ? `--featured-accent: ${generalConfig.featuredAccent};` : ""}
                ${generalConfig.featuredBtnText ? `--featured-btn-text: ${generalConfig.featuredBtnText};` : ""}
            }
        `;
    }, [generalConfig]);

    return (
        <>
            <AnalyticsTracker />
            <Routes>
            {/* Admin — no store layout */}
            <Route path="/admin" element={<AdminRoute />} />
            <Route path="/admin/*" element={<AdminRoute />} />
            <Route path="/activate-admin" element={<ActivateAdmin />} />

            {/* Public store */}
            <Route path="/*" element={
                <StoreLayout tickerItems={homeConfig.tickerItems} generalConfig={generalConfig}>
                    <Routes>
                        <Route path="/" element={<Home slides={homeConfig.bannerSlides} height={homeConfig.sliderHeight} generalConfig={generalConfig} bundleSection={homeConfig.bundleSection} seriesCards={homeConfig.seriesCards} />} />
                        <Route path="/shop" element={<><SEO pageId="shop" /><ProductCatalog /></>} />
                        <Route path="/book/:id" element={<ProductDetails />} />
                        <Route path="/series/:slug" element={<SeriesPage seriesCards={homeConfig.seriesCards} />} />
                        <Route path="/bundle/:id" element={<BundleDetails />} />
                        <Route path="/news" element={<News />} />
                        <Route path="/news/:id" element={<BlogPost />} />
                        <Route path="/local-bookstores" element={<LocalBookstores />} />
                        <Route path="/canvas" element={generalConfig?.canvasEnabled === false ? <Navigate to="/" replace /> : <Canvas />} />
                        <Route path="/about" element={<About generalConfig={generalConfig} />} />
                        <Route path="/contact" element={<Contact generalConfig={generalConfig} />} />
                        <Route path="/checkout" element={<Checkout generalConfig={generalConfig} />} />
                        <Route path="/order/:id" element={<OrderTracking />} />
                        <Route path="/download-otd/:tokenId" element={<OneTimeDownload />} />
                        <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                </StoreLayout>
            } />
            </Routes>
        </>
    );
}
